import { useParams, useNavigate } from "react-router-dom"
import {
  useEmployeeDetailsForm,
  useEmployeeService,
} from "../../employee/hooks/use-employee"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Badge } from "@/modules/shared/ui/components/badge"
import {
  Check,
  X,
  Flag,
  ArrowLeft,
  AlertCircle,
  Download,
  FileText,
} from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/modules/shared/ui/components/button"
import { RejectEmployeeDialog } from "../components/RejectEmployeeDialog"
import { FlagSectionDialog } from "../components/FlagFieldDialog"
import { onboardingService } from "../../infrastructure/onboarding-service"
import type { EmployeeOnboardingDetailsDto } from "@/modules/employee/employee/dto/employeeDetails"
import { FaFlag } from "react-icons/fa"

function ReadOnlyField({
  label,
  value,
  type,
}: {
  label: string
  value?: string | null
  type?: string
}) {
  const displayValue =
    value && value.trim() !== ""
      ? type === "date" && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(value + "T00:00:00").toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : value
      : null

  return (
    <div className="space-y-0.5">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      {displayValue ? (
        <p className="text-sm text-foreground py-1">{displayValue}</p>
      ) : (
        <p className="text-sm text-muted-foreground italic py-1">Not provided</p>
      )}
    </div>
  )
}
import { Onboardingstatus } from "../../onboarding-status"

const getStatusColor = (status?: string) => {
  if (!status) return "bg-muted text-foreground"
  switch (status.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-700"
    case "onboarding":
      return "bg-blue-100 text-blue-700"
    case "active":
      return "bg-green-100 text-green-700"
    default:
      return "bg-muted text-foreground"
  }
}

interface DocumentsSectionProps {
  employeeId: string
  requiredDocuments: string[]
  requiredDocumentUploads: {
    documentId: string
    documentName: string
    fileName: string
    contentType: string
    size: number
    submitted: boolean
    approved: boolean
  }[]
  documentsToBeSigned: {
    id: string
    fileName: string
    contentType: string
    size: number
    hasSigned: boolean
    approved: boolean
    signedDocumentId?: string | null
  }[]
  fieldFlags?: EmployeeOnboardingDetailsDto["fieldFlags"]
  onFlagSection: (sectionName: string) => void
  canFlag: boolean
  pendingNote?: string
}

interface SectionFlagButtonProps {
  sectionName: string
  onFlag: (sectionName: string) => void
  hasFlagged: boolean
  note?: string
}

function SectionFlagButton({
  sectionName,
  onFlag,
  hasFlagged,
  note,
}: SectionFlagButtonProps) {
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={() => onFlag(sectionName)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          hasFlagged
            ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
            : "bg-red-50 text-red-600 hover:bg-red-100"
        }`}
        title={`Flag ${sectionName}`}
        type="button"
      >
        <Flag className="w-3.5 h-3.5" />
        {hasFlagged ? "Rejected" : "Reject"}
      </button>
      {hasFlagged && note && (
        <p className="text-xs text-orange-600 max-w-xs text-right italic">
          "{note}"
        </p>
      )}
    </div>
  )
}

interface AdminProfileFormProps {
  formData: EmployeeOnboardingDetailsDto
  onFlagSection: (sectionName: string) => void
  canFlag: boolean
  pendingNote?: string
}

function AdminProfileForm({
  formData,
  onFlagSection,
  canFlag,
  pendingNote,
}: AdminProfileFormProps) {
  const sectionFlag = formData.fieldFlags?.find(
    (f) => f.fieldName === "personal" && !f.resolvedAt,
  )
  const hasSectionFlags = !!sectionFlag
  const isFlagged = hasSectionFlags || !!pendingNote
  const displayNote = sectionFlag?.note || pendingNote

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">Profile Details</h2>
        {canFlag && (
          <SectionFlagButton
            sectionName="Profile Details"
            onFlag={onFlagSection}
            hasFlagged={isFlagged}
            note={displayNote}
          />
        )}
      </div>
      {isFlagged && (
        <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
          <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium mb-1">
              {hasSectionFlags
                ? "This section has been rejected:"
                : "Flagged — pending rejection:"}
            </p>
            <p className="text-orange-700">{displayNote}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReadOnlyField label="First Name" value={formData.firstName} />
        <ReadOnlyField label="Last Name" value={formData.lastName} />
        <ReadOnlyField label="Gender" value={formData.gender} />
        <ReadOnlyField
          label="Date of Birth"
          value={formData.dateOfBirth}
          type="date"
        />
        <ReadOnlyField label="Phone Number" value={formData.phone} />
        <ReadOnlyField label="Email" value={formData.email} />
        <ReadOnlyField label="Position" value={formData.position} />
        <ReadOnlyField label="Tax Number" value={formData.taxNumber} />
        <ReadOnlyField label="Race Group" value={formData.raceGroup} />
        <ReadOnlyField
          label="Marital Status"
          value={formData.maritalStatus}
        />
      </div>
    </div>
  )
}

interface AdminAddressFormProps {
  formData: EmployeeOnboardingDetailsDto["address"]
  fieldFlags?: EmployeeOnboardingDetailsDto["fieldFlags"]
  onFlagSection: (sectionName: string) => void
  canFlag: boolean
  pendingNote?: string
}

function AdminAddressForm({
  formData,
  fieldFlags,
  onFlagSection,
  canFlag,
  pendingNote,
}: AdminAddressFormProps) {
  const sectionFlag = fieldFlags?.find(
    (f) => f.fieldName === "address" && !f.resolvedAt,
  )
  const hasSectionFlags = !!sectionFlag
  const isFlagged = hasSectionFlags || !!pendingNote
  const displayNote = sectionFlag?.note || pendingNote

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">Address Details</h2>
        {canFlag && (
          <SectionFlagButton
            sectionName="Address Details"
            onFlag={onFlagSection}
            hasFlagged={isFlagged}
            note={displayNote}
          />
        )}
      </div>

      {isFlagged && (
        <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
          <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium mb-1">
              {hasSectionFlags
                ? "This section has been rejected:"
                : "Flagged — pending rejection:"}
            </p>
            <p className="text-orange-700">{displayNote}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReadOnlyField label="Street Address" value={formData.line1} />
        <ReadOnlyField label="Address Line 2" value={formData.line2} />
        <ReadOnlyField label="City" value={formData.city} />
        <ReadOnlyField label="State" value={formData.state} />
        <ReadOnlyField label="ZIP / Postal Code" value={formData.postalCode} />
        <div className="sm:col-span-2">
          <ReadOnlyField label="Country" value={formData.country} />
        </div>
      </div>
    </div>
  )
}

interface AdminBankFormProps {
  formData: EmployeeOnboardingDetailsDto["bankDetails"]
  fieldFlags?: EmployeeOnboardingDetailsDto["fieldFlags"]
  onFlagSection: (sectionName: string) => void
  canFlag: boolean
  pendingNote?: string
}

function AdminBankForm({
  formData,
  fieldFlags,
  onFlagSection,
  canFlag,
  pendingNote,
}: AdminBankFormProps) {
  const sectionFlag = fieldFlags?.find(
    (f) => f.fieldName === "bank" && !f.resolvedAt,
  )
  const hasSectionFlags = !!sectionFlag
  const isFlagged = hasSectionFlags || !!pendingNote
  const displayNote = sectionFlag?.note || pendingNote

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">Bank Details</h2>
        {canFlag && (
          <SectionFlagButton
            sectionName="Bank Details"
            onFlag={onFlagSection}
            hasFlagged={isFlagged}
            note={displayNote}
          />
        )}
      </div>

      {isFlagged && (
        <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
          <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium mb-1">
              {hasSectionFlags
                ? "This section has been rejected:"
                : "Flagged — pending rejection:"}
            </p>
            <p className="text-orange-700">{displayNote}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReadOnlyField
          label="Account Holder Name"
          value={formData.accountHolderName}
        />
        <ReadOnlyField label="Bank Name" value={formData.bankName} />
        <ReadOnlyField label="Account Number" value={formData.accountNumber} />
        <ReadOnlyField label="Branch Code" value={formData.branchCode} />
        <ReadOnlyField label="Account Type" value={formData.accountType} />
      </div>
    </div>
  )
}

interface AdminEmergencyContactFormProps {
  formData: {
    emergencyContactName: string
    emergencyContactPhone: string
  }
  fieldFlags?: EmployeeOnboardingDetailsDto["fieldFlags"]
  onFlagSection: (sectionName: string) => void
  canFlag: boolean
  pendingNote?: string
}

function AdminEmergencyContactForm({
  formData,
  fieldFlags,
  onFlagSection,
  canFlag,
  pendingNote,
}: AdminEmergencyContactFormProps) {
  const sectionFlag = fieldFlags?.find(
    (f) => f.fieldName === "emergency" && !f.resolvedAt,
  )
  const hasSectionFlags = !!sectionFlag
  const isFlagged = hasSectionFlags || !!pendingNote
  const displayNote = sectionFlag?.note || pendingNote

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">Emergency Contact</h2>
        {canFlag && (
          <SectionFlagButton
            sectionName="Emergency Contact"
            onFlag={onFlagSection}
            hasFlagged={isFlagged}
            note={displayNote}
          />
        )}
      </div>

      {isFlagged && (
        <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
          <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium mb-1">
              {hasSectionFlags
                ? "This section has been rejected:"
                : "Flagged — pending rejection:"}
            </p>
            <p className="text-orange-700">{displayNote}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReadOnlyField
          label="Contact Name"
          value={formData.emergencyContactName}
        />
        <ReadOnlyField
          label="Contact Phone"
          value={formData.emergencyContactPhone}
        />
      </div>
    </div>
  )
}

function DocumentsSection({
  employeeId,
  requiredDocuments,
  requiredDocumentUploads,
  documentsToBeSigned,
  fieldFlags,
  onFlagSection,
  canFlag,
  pendingNote,
}: DocumentsSectionProps) {
  const sectionFlag = fieldFlags?.find(
    (f) => f.fieldName === "documents" && !f.resolvedAt,
  )
  const hasSectionFlags = !!sectionFlag
  const isFlagged = hasSectionFlags || !!pendingNote
  const displayNote = sectionFlag?.note || pendingNote

  const handleDownload = async (documentId: string) => {
    try {
      const result = await onboardingService.downloadEmployeeDocument(
        employeeId,
        documentId,
      )
      window.open(result.downloadUrl, "_blank")
    } catch {
      toast.error("Failed to download document. Please try again.")
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">Documents</h2>
        {canFlag && (
          <SectionFlagButton
            sectionName="Documents"
            onFlag={onFlagSection}
            hasFlagged={isFlagged}
            note={displayNote}
          />
        )}
      </div>

      {isFlagged && (
        <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
          <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium mb-1">
              {hasSectionFlags
                ? "This section has been rejected:"
                : "Flagged — pending rejection:"}
            </p>
            <p className="text-orange-700">{displayNote}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Required Documents — with download for each */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">
            Required Documents
          </h3>
          {requiredDocuments.length > 0 ? (
            <div className="space-y-2">
              {requiredDocuments.map((docName, index) => {
                const uploaded = requiredDocumentUploads.find(
                  (d) => d.documentName === docName,
                )
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{docName}</span>
                        {uploaded && (
                          <p className="text-xs text-muted-foreground">
                            {uploaded.fileName} ({formatFileSize(uploaded.size)}
                            )
                          </p>
                        )}
                      </div>
                    </div>
                    {uploaded ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(uploaded.documentId)}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        Not uploaded
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No required documents</p>
          )}
        </div>

        {/* Documents to be Signed */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">
            Documents to be Signed
          </h3>
          {documentsToBeSigned.length > 0 ? (
            <div className="space-y-2">
              {documentsToBeSigned.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {doc.fileName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(doc.size)}
                        {doc.hasSigned && (
                          <span className="ml-2 text-green-600 font-medium">
                            Signed
                          </span>
                        )}
                        {!doc.hasSigned && (
                          <span className="ml-2 text-orange-500 font-medium">
                            Pending signature
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handleDownload(
                        doc.hasSigned && doc.signedDocumentId
                          ? doc.signedDocumentId
                          : doc.id,
                      )
                    }
                  >
                    <Download className="w-3 h-3 mr-1" />
                    {doc.hasSigned && doc.signedDocumentId
                      ? "Download Signed"
                      : "Download"}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No documents to be signed</p>
          )}
        </div>
      </div>

      {requiredDocuments.length === 0 &&
        requiredDocumentUploads.length === 0 &&
        documentsToBeSigned.length === 0 && (
          <p className="text-sm text-muted-foreground">No documents</p>
        )}
    </div>
  )
}

export default function ReviewEmployeeInformationPage() {
  const { employeeId } = useParams<{ employeeId: string }>()
  const navigate = useNavigate()
  const {
    data: employee,
    isLoading,
    error,
    refetch,
  } = useEmployeeDetailsForm(employeeId!)
  const { acceptEmployee, flagEmployeeSections } = useEmployeeService()
  const [_showRejectDialog, _setShowRejectDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingFlags, setPendingFlags] = useState<
    { fieldName: string; note: string }[]
  >([])
  const [hasShownError, setHasShownError] = useState(false)
  const [flagDialog, setFlagDialog] = useState<{
    open: boolean
    sectionName: string
  }>({ open: false, sectionName: "" })

  useEffect(() => {
    if (employeeId) {
      refetch()
    }
  }, [employeeId, refetch])

  useEffect(() => {
    if (error && !hasShownError) {
      toast.error("Failed to load employee details")
      setHasShownError(true)
    }
  }, [error, hasShownError])

  const handleAccept = async () => {
    setIsSubmitting(true)
    try {
      await acceptEmployee(employeeId!)
      setPendingFlags([])
      toast.success("Employee accepted successfully")
      navigate("/onboarding/employees")
    } catch (error: unknown) {
      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string } }
            message?: string
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        "Failed to accept employee"
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (!employee?.onboardingId) {
      toast.error("Onboarding ID not found")
      return
    }

    if (pendingFlags.length === 0) {
      toast.error(
        "Please flag at least one section before rejecting the employee",
      )
      return
    }

    setIsSubmitting(true)
    try {
      await flagEmployeeSections(employee.onboardingId, pendingFlags)
      setPendingFlags([])
      toast.success("Employee rejected — flags sent to employee")
      refetch()
      navigate("/onboarding/employees")
    } catch (error) {
      const errorMessage =
        (error as Error)?.message || "Failed to reject employee"
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFlagSection = async (note: string) => {
    if (!employee?.onboardingId) {
      toast.error("Onboarding ID not found")
      return
    }

    // Map UI section names to backend section names
    const sectionNameMap: Record<string, string> = {
      "Profile Details": "personal",
      "Emergency Contact": "emergency",
      "Address Details": "address",
      "Bank Details": "bank",
      Documents: "documents",
    }

    const backendSectionName = sectionNameMap[flagDialog.sectionName]
    if (!backendSectionName) {
      toast.error("Invalid section name")
      return
    }

    setPendingFlags((prev) => {
      const filtered = prev.filter((f) => f.fieldName !== backendSectionName)
      return [...filtered, { fieldName: backendSectionName, note }]
    })
    toast.success(`${flagDialog.sectionName} flagged — click Reject to send`)
  }

  // No longer needed - we use section names directly now
  // const getSectionFieldNames = (sectionName: string): string[] => { ... }

  const convertToFormData = (): EmployeeOnboardingDetailsDto | null => {
    if (!employee) return null

    // Parse address and bankDetails if they are JSON strings
    const parseAddress = () => {
      const empty = {
        line1: "",
        line2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      }
      if (!employee.address) return empty
      if (typeof employee.address === "string") {
        try {
          const p = JSON.parse(employee.address)
          return {
            line1: p.Line1 ?? p.line1 ?? "",
            line2: p.Line2 ?? p.line2 ?? "",
            city: p.City ?? p.city ?? "",
            state: p.State ?? p.state ?? "",
            postalCode: p.PostalCode ?? p.postalCode ?? "",
            country: p.Country ?? p.country ?? "",
          }
        } catch {
          return empty
        }
      }
      const a = employee.address as Record<string, string>
      return {
        line1: a.Line1 ?? a.line1 ?? "",
        line2: a.Line2 ?? a.line2 ?? "",
        city: a.City ?? a.city ?? "",
        state: a.State ?? a.state ?? "",
        postalCode: a.PostalCode ?? a.postalCode ?? "",
        country: a.Country ?? a.country ?? "",
      }
    }

    const parseBankDetails = () => {
      const empty = {
        bankName: "",
        accountNumber: "",
        branchCode: "",
        accountHolderName: "",
        accountType: "",
      }
      if (!employee.bankDetails) return empty
      if (typeof employee.bankDetails === "string") {
        try {
          const p = JSON.parse(employee.bankDetails)
          return {
            bankName: p.BankName ?? p.bankName ?? "",
            accountNumber: p.AccountNumber ?? p.accountNumber ?? "",
            branchCode: p.BranchCode ?? p.branchCode ?? "",
            accountHolderName: p.AccountHolderName ?? p.accountHolderName ?? "",
            accountType: p.AccountType ?? p.accountType ?? "",
          }
        } catch {
          return empty
        }
      }
      const b = employee.bankDetails as Record<string, string>
      return {
        bankName: b.BankName ?? b.bankName ?? "",
        accountNumber: b.AccountNumber ?? b.accountNumber ?? "",
        branchCode: b.BranchCode ?? b.branchCode ?? "",
        accountHolderName: b.AccountHolderName ?? b.accountHolderName ?? "",
        accountType: b.AccountType ?? b.accountType ?? "",
      }
    }

    return {
      onboardingId: employee.onboardingId,
      onboardingStatus: employee.onboardingStatus,
      seenWelcome: employee.seenWelcome,
      inviteSent: employee.inviteSent,
      notes: employee.notes || [],
      requiredDocuments: employee.requiredDocuments || [],
      onboardingCreatedAt: employee.onboardingCreatedAt || "",
      onboardingUpdatedAt: employee.onboardingUpdatedAt || "",
      onboardingStartDate: employee.onboardingStartDate || "",
      latest: employee.latest || false,
      previousOnboardingId: employee.previousOnboardingId || "",
      organizationId: employee.organizationId || "",
      organizationName: employee.organizationName || "",
      employeeId: employee.employeeId,
      identityType: employee.identityType || "",
      identityNumber: employee.identityNumber || "",
      firstName: employee.firstName,
      lastName: employee.lastName,
      employeeStatus: employee.employeeStatus,
      email: employee.email,
      phone: employee.phone || "",
      position: employee.position || "",
      employeeStartDate: employee.employeeStartDate || "",
      dateOfBirth: (() => {
        const dob = employee.dateOfBirth || ""
        if (!dob) return ""
        if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob
        if (/^\d{4}\/\d{2}\/\d{2}$/.test(dob)) return dob.replace(/\//g, "-")
        const d = new Date(dob)
        if (!isNaN(d.getTime())) return d.toISOString().split("T")[0]
        return dob
      })(),
      gender: employee.gender || "",
      taxNumber: employee.taxNumber || "",
      raceGroup: employee.raceGroup || "",
      maritalStatus: employee.maritalStatus || "",
      emergencyContactName: employee.emergencyContactName || "",
      emergencyContactPhone: employee.emergencyContactPhone || "",
      address: parseAddress(),
      bankDetails: parseBankDetails(),
      employeeCreatedAt: employee.employeeCreatedAt || "",
      employeeUpdatedAt: employee.employeeUpdatedAt || "",
      fieldFlags: employee.notes || [],
      documentsToBeSigned: Array.from(
        new Map(
          (employee.documentsToBeSigned || []).map((d) => [d.id, d]),
        ).values(),
      ),
      documents: employee.documents || [],
    }
  }

  const formData = convertToFormData()

  if (isLoading && !employee) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  if (!employee || !formData) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground">Employee not found</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="bg-card rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {employee.firstName} {employee.lastName}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {employee.position || "No position assigned"}
              </p>
            </div>
            {employee?.onboardingStatus === Onboardingstatus.SUBMITTED && (
              <div className="flex items-center gap-3">
                <Badge className={getStatusColor(employee.employeeStatus)}>
                  {employee.employeeStatus}
                </Badge>
                <Button
                  onClick={handleAccept}
                  disabled={isSubmitting}
                  className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  Accept
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={isSubmitting}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 gap-1.5"
                >
                  <X className="w-4 h-4" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </div>

        {employee.onboardingStatus === Onboardingstatus.SUBMITTED && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900 text-sm">
                    Review Employee Details
                  </h3>
                  <p className="text-sm text-blue-800 mt-1">
                    Carefully review all employee information below. If you find
                    any incorrect or incomplete details, Reject the section to
                    notify the employee. They will be able to see your notes and
                    resubmit the corrected information.
                  </p>
                </div>
              </div>
            </div>

            {/* RejectEmployeeDialog kept but disconnected — reject now uses section flags directly */}
            <RejectEmployeeDialog
              open={_showRejectDialog}
              onOpenChange={_setShowRejectDialog}
              onReject={async () => {}}
            />

            <FlagSectionDialog
              open={flagDialog.open}
              onOpenChange={(open: boolean) =>
                setFlagDialog({ ...flagDialog, open })
              }
              onFlag={handleFlagSection}
              sectionName={flagDialog.sectionName}
            />
          </>
        )}

        <div className="bg-card rounded-lg shadow-sm p-6 space-y-6">
          <AdminProfileForm
            formData={formData}
            canFlag={
              employee?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            pendingNote={
              pendingFlags.find((f) => f.fieldName === "personal")?.note
            }
            onFlagSection={(sectionName) =>
              setFlagDialog({
                open: true,
                sectionName,
              })
            }
          />

          <AdminEmergencyContactForm
            formData={{
              emergencyContactName: formData.emergencyContactName,
              emergencyContactPhone: formData.emergencyContactPhone,
            }}
            fieldFlags={formData.fieldFlags}
            canFlag={
              employee?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            pendingNote={
              pendingFlags.find((f) => f.fieldName === "emergency")?.note
            }
            onFlagSection={(sectionName) =>
              setFlagDialog({
                open: true,
                sectionName,
              })
            }
          />

          <AdminAddressForm
            formData={formData.address}
            fieldFlags={formData.fieldFlags}
            canFlag={
              employee?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            pendingNote={
              pendingFlags.find((f) => f.fieldName === "address")?.note
            }
            onFlagSection={(sectionName) =>
              setFlagDialog({
                open: true,
                sectionName,
              })
            }
          />

          <AdminBankForm
            formData={formData.bankDetails}
            fieldFlags={formData.fieldFlags}
            canFlag={
              employee?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            pendingNote={
              pendingFlags.find((f) => f.fieldName === "bank")?.note
            }
            onFlagSection={(sectionName) =>
              setFlagDialog({
                open: true,
                sectionName,
              })
            }
          />

          <DocumentsSection
            employeeId={employee.employeeId}
            requiredDocuments={formData.requiredDocuments || []}
            requiredDocumentUploads={employee.requiredDocumentUploads || []}
            documentsToBeSigned={formData.documentsToBeSigned || []}
            fieldFlags={formData.fieldFlags}
            canFlag={
              employee?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            pendingNote={
              pendingFlags.find((f) => f.fieldName === "documents")?.note
            }
            onFlagSection={(sectionName) =>
              setFlagDialog({
                open: true,
                sectionName,
              })
            }
          />
        </div>
      </div>
    </div>
  )
}
